// ---------------------------------------------------------------------------
// Diagnostics retention — Safe Mode always has history
//
// Boot steps and console errors are kept on the device so a crash that happens
// after a restart still has the trail that led to it. How far back that trail
// goes is configurable: anything older than the retention window is pruned the
// next time the history is read.
// ---------------------------------------------------------------------------

const KEY = "sophia:diag-retention-days";
export const DEFAULT_RETENTION_DAYS = 7;
export const RETENTION_CHOICES = [1, 3, 7, 14, 30] as const;

/** Retention window in days (1–90). */
export function retentionDays(): number {
  try {
    const raw = Number(localStorage.getItem(KEY));
    if (Number.isFinite(raw) && raw >= 1 && raw <= 90) return raw;
  } catch { /* private mode */ }
  return DEFAULT_RETENTION_DAYS;
}

export function setRetentionDays(days: number): void {
  const clamped = Math.min(90, Math.max(1, Math.round(days)));
  try { localStorage.setItem(KEY, String(clamped)); } catch { /* best effort */ }
  try { window.dispatchEvent(new CustomEvent("sophia:diag-retention", { detail: clamped })); } catch { /* noop */ }
}

/** Oldest timestamp still worth keeping. */
export function retentionCutoff(): number {
  return Date.now() - retentionDays() * 86_400_000;
}

/** Drop entries older than the window, keeping newest-first order. */
export function pruneByRetention<T>(entries: T[], at: (entry: T) => number): T[] {
  const cutoff = retentionCutoff();
  return entries.filter((e) => {
    const t = at(e);
    return !Number.isFinite(t) || t >= cutoff;
  });
}
